import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { IMAGES } from "@/data";
import { Reveal, SectionHead, EASE } from "@/components/Reveal";

const CASE_SWAP = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.45, ease: EASE },
};
import { BookButton } from "@/components/Buttons";

const CASES = [
  {
    id: "whitening",
    label: "Whitening",
    image: IMAGES.whitening,
    title: "In-chair whitening",
    note: "A few shades brighter in one 90-minute visit, gums protected the whole way. Most people go straight back to work afterwards.",
  },
  {
    id: "makeover",
    label: "Smile Makeover",
    image: IMAGES.transform,
    title: "Smile makeover",
    note: "A couple of veneers, some whitening and a little reshaping over three visits. Still unmistakably her smile — just the version she'd been hoping for.",
  },
  {
    id: "aligners",
    label: "Clear Aligners",
    image: IMAGES.children,
    title: "Gently straightened",
    note: "A crowded, crooked smile brought into line with clear aligners over several months — no brackets, no wires, and barely noticeable along the way.",
  },
];

const HANDLE_MIN_PCT = 4;
const HANDLE_MAX_PCT = 96;
const HANDLE_START_PCT = 50;

const BeforeAfter = ({ image }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(HANDLE_START_PCT);
  const dragging = useRef(false);

  const move = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(HANDLE_MAX_PCT, Math.max(HANDLE_MIN_PCT, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative aspect-[16/10] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-2xl border border-leaf/20"
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        move(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && move(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      <img src={image} alt="After treatment" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={image}
          alt="Before treatment"
          draggable={false}
          className="h-full w-full object-cover"
          style={{ filter: "grayscale(0.9) brightness(0.62) sepia(0.25)" }}
        />
      </div>
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -left-px w-0.5 bg-leaf" />
        <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full border-2 border-forest bg-leaf text-forest shadow-xl">
          <MoveHorizontal className="h-5 w-5" />
        </div>
      </div>
      <span className="absolute left-4 top-4 rounded-full bg-forest/70 px-3 py-1 font-mono text-xs tracking-[0.25em] uppercase text-cream backdrop-blur">
        Before
      </span>
      <span className="absolute right-4 top-4 rounded-full bg-cream px-3 py-1 font-mono text-xs tracking-[0.25em] uppercase text-forest">
        After
      </span>
    </div>
  );
};

const Gallery = () => {
  const [active, setActive] = useState(CASES[0]);

  return (
    <section id="gallery" data-testid="smile-gallery-section" className="bg-forest py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
        <SectionHead
          overline="Smile gallery"
          title={
            <>
              Have a look{" "}
              <span className="italic text-stone">for yourself.</span>
            </>
          }
        />
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-xl font-sans text-sm leading-relaxed text-mist sm:text-base">
            Drag the handle across to compare. These are the sorts of results
            we work towards — and when it's your turn, we'll take before-and-after
            photos of your own teeth so you can see the difference properly.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <div data-testid="gallery-filter-tabs" className="mt-10 flex flex-wrap gap-3">
            {CASES.map((c) => (
              <button
                key={c.id}
                data-testid={`gallery-tab-${c.id}`}
                onClick={() => setActive(c)}
                className={`rounded-full border px-5 py-2.5 font-sans text-sm transition-[background-color,color,border-color] duration-300 ${
                  active.id === c.id
                    ? "border-cream bg-cream text-forest"
                    : "border-cream/20 text-mist hover:border-leaf/50 hover:text-leaf"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.25} className="mt-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              {...CASE_SWAP}
            >
              <BeforeAfter image={active.image} />
              <div className="mt-6 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-serif text-xl text-cream sm:text-2xl">{active.title}</h3>
                  <p className="mt-2 max-w-lg font-sans text-sm text-mist">{active.note}</p>
                </div>
                <BookButton variant="light" testid="gallery-book-button" className="shrink-0">
                  Chat about your smile
                </BookButton>
              </div>
            </motion.div>
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
};

export default Gallery;
